import { Component, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, inject, signal, viewChild } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment-timezone';
import {
  getInfluencersSyncError,
  getSyncInfluencers,
  InfluencersSync,
  InfluencersSyncStatus,
  IRegistrationPlatformState,
  ResetRegistrationPlatformState,
} from '@app/stores';
import {SocketService} from '@app/services/socket.service';
import {LoaderService} from '@app/services/loader.service';
import {
  registrationPlatformSettingsRefusal,
  RegistrationPlatformService,
} from '@app/services/registration-platform.service';
import {TaskLogsComponent} from '@app/_components/task-logs/task-logs.component';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import {SbCheckboxComponent} from '@app/_components/unified/sb-checkbox/sb-checkbox.component';
import {SbTableColumn, SbTableOptions, SbTableRowBase} from '@app/_components/unified/sb-table/sb-table.types';
import {ProjectRegistrationPlatform} from '@app/modules/shared/models/project-registration-platform';
import {SbSchemaServerErrors} from '@app/_components/unified/sb-schema-form/sb-schema-form.types';
import {RegistrationPlatformInstallModalComponent} from '../registration-platform-install-modal/registration-platform-install-modal.component';
import {RegistrationPlatformSettingsModalComponent} from '../registration-platform-settings-modal/registration-platform-settings-modal.component';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbButtonComponent } from '../../../_components/unified/sb-button/sb-button.component';
import { SbTableComponent } from '../../../_components/unified/sb-table/sb-table.component';
import { SbTableToolbarDirective, SbTableCardDirective, SbTableCellDirective } from '../../../_components/unified/sb-table/sb-table.directives';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { RegistrationPlatformLogoComponent } from './registration-platform-logo.component';
import { SbCheckboxComponent as SbCheckboxComponent_1 } from '../../../_components/unified/sb-checkbox/sb-checkbox.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';

/** What the last-sync badge says, and its colour, per task log status. */
const SYNC_STATUS: Record<string, { label: string; badge: string }> = {
  finished: {label: 'Finished', badge: 'text-bg-success'},
  failed: {label: 'Failed', badge: 'text-bg-danger'},
  queued: {label: 'Queued', badge: 'text-bg-secondary'},
  started: {label: 'Running', badge: 'text-bg-info'},
  'in-progress': {label: 'Running', badge: 'text-bg-info'},
};

export type RegistrationPlatformsViewType = 'card' | 'list';

/** Where this browser remembers the chosen view (a per-viewer convenience, like the projects list). */
export const VIEW_TYPE_KEY = 'registrationPlatformsViewType';

const readViewType = (): RegistrationPlatformsViewType => {
  try {
    return localStorage.getItem(VIEW_TYPE_KEY) === 'card' ? 'card' : 'list';
  } catch (e) {
    return 'list';
  }
};

/**
 * The project's registration platforms: its installs (a platform may be installed more than once,
 * each under its own name), with their active switch, last sync, push and actions. Settings are a
 * form each plugin describes (settings modal); platforms are added from the catalogue (install popup).
 */
@Component({
  selector: 'app-thirdparty-platforms',
  templateUrl: './thirdparty-platforms.component.html',
  styleUrls: ['./thirdparty-platforms.component.css', './thirdparty-platforms-api-modal.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, SbButtonComponent, SbTableComponent, SbTableToolbarDirective, SbTableCardDirective, NgTemplateOutlet, SbTableCellDirective, RegistrationPlatformLogoComponent, SbCheckboxComponent_1, ReactiveFormsModule, FormsModule, MatTooltip, AsyncPipe]
})
export class ThirdpartyPlatformsComponent implements OnInit, OnDestroy {
  private readonly registrationPlatformStore = inject<Store<IRegistrationPlatformState>>(Store);
  private readonly registrationPlatformService = inject(RegistrationPlatformService);
  private readonly route = inject(ActivatedRoute);
  private readonly loaderService = inject(LoaderService);
  private readonly toastr = inject(ToastrService);
  private readonly socketService = inject(SocketService);
  private readonly modalService = inject(NgbModal);
  private readonly sbModal = inject(SbModalService);

  readonly logsTable = viewChild<TemplateRef<any>>('logsTable');

  projectId: number;
  readonly platforms = signal<ProjectRegistrationPlatform[]>([]);
  readonly canManage = signal(false);
  readonly loaded = signal(false);
  readonly loadError = signal('');
  /** The row whose switch is saving or whose removal runs (its install id). */
  readonly actingId = signal<number | null>(null);

  readonly columns: SbTableColumn[] = [
    {id: 'platform', header: 'Platform', sortable: false},
    {id: 'status', header: 'Status', sortable: false},
    {id: 'last_sync', header: 'Last sync', sortable: false},
    {id: 'push', header: 'Push', sortable: false},
    {id: 'actions', header: '', sortable: false, className: 'text-end'},
  ];
  /** Cards or a table, as on the projects list: cards unless this browser chose the list. */
  viewType: RegistrationPlatformsViewType = readViewType();
  readonly tableOptions: SbTableOptions = {
    tableId: 'registration-platforms-table',
    searching: false,
    pageLength: 25,
    lengthMenu: [25, 50, 100],
    order: [],
    emptyMessage: 'No API integrations added yet.',
    // No bulk actions.
    selectable: false,
  };

  // The sync: started here or seen on the socket, keyed by the install id.
  loader$: Observable<boolean>;
  importData: any;
  importJobId: number | null = null;
  readonly importProgress = signal<{ status: string; progress: number }>({status: 'started', progress: 0});
  readonly currentSyncId = signal(0);
  loaderImport = new BehaviorSubject<boolean>(false);
  loaderImport$ = this.loaderImport.asObservable();
  /** The Invalid Records table: one column per field the sync reported, one row per refused record. */
  importColumns: SbTableColumn[] = [];
  importRows: SbTableRowBase[] = [];
  readonly importTableOptions: SbTableOptions = {
    selectable: false,
    ordering: false,
    pageLength: 10,
    lengthMenu: [10, 25, 50, 100],
  };

  private readonly unsubscriber = new Subject<void>();
  private socketSubscription: Subscription | null = null;

  constructor() {
    this.socketService.resetSocketData();
    this.registrationPlatformStore.dispatch(ResetRegistrationPlatformState({params: {error: '', influencersSync: null}}));
  }

  ngOnInit(): void {
    this.loader$ = this.loaderService.loader$;
    this.route.params.pipe(takeUntil(this.unsubscriber)).subscribe((params) => {
      if (params.projectId) {
        this.projectId = +params.projectId;
        this.load();
      }
    });
    this.subscribeStores();
    this.subscribeToSocket();
  }

  changeViewType(type: RegistrationPlatformsViewType): void {
    this.viewType = type;
    try {
      localStorage.setItem(VIEW_TYPE_KEY, type);
    } catch (e) {
      // Storage refused (private window): the choice lasts for this page only.
    }
  }

  ngOnDestroy(): void {
    this.socketSubscription?.unsubscribe();
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  get rows(): SbTableRowBase[] {
    return this.platforms() as unknown as SbTableRowBase[];
  }

  asRow(row: SbTableRowBase): ProjectRegistrationPlatform {
    return row as unknown as ProjectRegistrationPlatform;
  }

  load(): void {
    this.registrationPlatformService.getProjectPlatforms(this.projectId).subscribe({
      next: (list) => {
        this.platforms.set(list?.platforms || []);
        this.canManage.set(!!list?.can_manage);
        this.loaded.set(true);
        this.loadError.set('');
      },
      error: (error) => {
        this.loaded.set(true);
        this.loadError.set(error?.error?.message || 'The API integrations could not be loaded.');
      },
    });
  }

  // ── Add, edit, switch, remove ────────────────────────────────────────────

  openInstall(): void {
    this.sbModal.open({
      title: 'Add API integration',
      bodyComponent: RegistrationPlatformInstallModalComponent,
      modalData: {projectId: this.projectId},
      variant: 'form',
      size: 'lg',
      showFooter: false,
    }).result.then((row: ProjectRegistrationPlatform) => {
      if (!row) {
        return;
      }
      this.load();
      this.openSettings(row, true);
    }, () => this.load()); // a failed install (e.g. added meanwhile elsewhere) may have changed the list
  }

  /**
   * The settings modal; `activate` saves the platform as active too, `refusal` marks the fields a
   * refused save named (`{message, errors, form_errors}`). Resolves with the saved row (the list
   * reloads), or null when nothing was saved.
   */
  openSettings(
    row: ProjectRegistrationPlatform,
    activate = false,
    refusal: (SbSchemaServerErrors & { message?: string }) | null = null,
  ): Promise<ProjectRegistrationPlatform | null> {
    return this.sbModal.open({
      title: this.platformName(row),
      bodyComponent: RegistrationPlatformSettingsModalComponent,
      modalData: {projectId: this.projectId, instanceId: row.id, activate, ...(refusal ? {refusal} : {})},
      variant: 'form',
      size: 'lg',
      showFooter: false,
    }).result.then((saved: ProjectRegistrationPlatform | null) => {
      this.replaceRow(saved || null);
      this.load();
      return saved || null;
    }, () => null);
  }

  /**
   * On: a platform whose settings are incomplete opens them (saving activates it); a complete one
   * is switched on, and when its stored settings no longer pass, they open with the server's
   * messages. Off: switched off. The switch shows the saved state, or goes back when nothing was saved.
   */
  onActiveChange(row: ProjectRegistrationPlatform, active: boolean, toggle?: SbCheckboxComponent): void {
    const revert = () => toggle?.writeValue(row.active);
    // NgModel keeps the clicked value as its view value, so a saved row is written back explicitly.
    const settle = (saved: ProjectRegistrationPlatform | null) => toggle?.writeValue(saved ? !!saved.active : row.active);
    if (active && !row.integration) {
      this.openSettings(row, true).then(settle);
      return;
    }
    this.actingId.set(row.id);
    this.registrationPlatformService.saveProjectPlatform(this.projectId, row.id, {active}).subscribe({
      next: (updated) => {
        this.actingId.set(null);
        this.replaceRow(updated);
        this.toastr.success(`${this.platformName(row)} ${active ? 'activated' : 'deactivated'}`);
      },
      error: (error) => {
        this.actingId.set(null);
        const refusal = active ? registrationPlatformSettingsRefusal(error) : null;
        if (refusal) {
          // The stored settings fail the platform's form: open them with the server's messages.
          this.openSettings(row, true, {message: error?.error?.message || '', ...refusal}).then(settle);
          return;
        }
        revert();
        this.toastr.error(error?.error?.message || 'The platform could not be updated.', 'Error');
      },
    });
  }

  remove(row: ProjectRegistrationPlatform): void {
    const name = this.escape(row.name || row.platform?.name || 'this platform');
    this.sbModal.confirm(
      'Remove API integration?',
      `<p>Remove <strong>${name}</strong> from this project? Its settings and credentials are deleted.</p>`
      + `<p class="mb-0">Contacts it synced stay, but are no longer linked to ${name}, so another API integration can update them.</p>`,
      'Remove',
    ).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.actingId.set(row.id);
      this.registrationPlatformService.removeProjectPlatform(this.projectId, row.id).subscribe({
        next: () => {
          this.actingId.set(null);
          this.toastr.success(`${this.platformName(row)} removed`);
          this.load();
        },
        error: (error) => {
          this.actingId.set(null);
          this.toastr.error(error?.error?.message || 'The platform could not be removed.', 'Error');
        },
      });
    });
  }

  // ── Cells ────────────────────────────────────────────────────────────────

  lastSyncRelative(row: ProjectRegistrationPlatform): string {
    return row.last_sync?.at ? moment(row.last_sync.at).fromNow() : '';
  }

  lastSyncExact(row: ProjectRegistrationPlatform): string {
    return row.last_sync?.at ? moment(row.last_sync.at).format('DD MMM, YYYY h:mm A') : '';
  }

  syncStatusLabel(row: ProjectRegistrationPlatform): string {
    return SYNC_STATUS[row.last_sync?.status]?.label || row.last_sync?.status || '';
  }

  syncStatusBadge(row: ProjectRegistrationPlatform): string {
    return SYNC_STATUS[row.last_sync?.status]?.badge || 'text-bg-secondary';
  }

  /** Why the syncs do not push to the platform, for a plugin that could. */
  pushOffReason(row: ProjectRegistrationPlatform): string {
    if (!row.push?.enabled) {
      return 'Push is turned off in this platform\'s settings.';
    }
    if (!row.push?.allowed) {
      return 'Push is not allowed for this project on this environment.';
    }
    return 'Push runs once the platform is active.';
  }

  /** Why share links are not pushed, for a plugin that could push them. */
  linksOffReason(row: ProjectRegistrationPlatform): string {
    if (!row.push?.enabled) {
      return 'Push is turned off in this platform\'s settings, and share links are part of it.';
    }
    if (!row.push?.links_enabled) {
      return 'Share links are turned off in this platform\'s settings.';
    }
    if (!row.push?.allowed) {
      return 'Share links are not allowed for this project on this environment.';
    }
    if (!row.push?.ready) {
      return 'The settings are missing what the share-link push needs.';
    }
    return 'Share links are pushed once the platform is active.';
  }

  /** The catalogue platform's name under the install's, when the install was named otherwise. */
  catalogueName(row: ProjectRegistrationPlatform): string {
    const catalogue = row.platform?.name || '';
    return catalogue && row.name && row.name.trim().toLowerCase() !== catalogue.trim().toLowerCase() ? catalogue : '';
  }

  /** The switch's accessible name: the platform and its state. */
  switchLabel(row: ProjectRegistrationPlatform): string {
    return `${this.platformName(row)}: ${row.active ? 'Active' : 'Inactive'}`;
  }

  /** The Sync button's accessible name, with the running sync's progress. */
  syncAriaLabel(row: ProjectRegistrationPlatform): string {
    const name = `Sync ${this.platformName(row)}`;
    return this.isSyncing(row) && this.loaderImport.value ? `${name}: ${this.syncProgressLabel()}` : name;
  }

  /** The install's name (the catalogue name unless renamed). */
  platformName(row: ProjectRegistrationPlatform): string {
    return row.name || row.platform?.name || 'API integration';
  }

  /** Active, and its settings complete enough to sync. */
  canSync(row: ProjectRegistrationPlatform): boolean {
    return !!row.active && !!row.integration;
  }

  isSyncing(row: ProjectRegistrationPlatform): boolean {
    return this.currentSyncId() === row.id;
  }

  syncProgressLabel(): string {
    return `${this.importProgress().status || 'started'} ${this.importProgress().progress || 0}%`;
  }

  // ── Sync ─────────────────────────────────────────────────────────────────

  submitInfluencerSync(row: ProjectRegistrationPlatform): void {
    this.currentSyncId.set(row.id);
    this.importProgress.set({status: 'started', progress: 0});
    this.loaderImport.next(true);
    // The install syncs; its catalogue id still goes along for an older server.
    this.registrationPlatformStore.dispatch(InfluencersSync({
      data: {project_id: this.projectId, project_registration_platform_id: row.id, registration_platform_id: row.registration_platform_id}
    }));
  }

  showPreviousSyncLogs(row: ProjectRegistrationPlatform): void {
    const modelRef = this.sbModal.openComponent(TaskLogsComponent, {centered: false, size: 'xl', keyboard: true}, {
      data: {
        campaign_id: '',
        influencer_type_id: '',
        project_id: this.projectId,
        action: 'sync',
        registration_platform_id: row.registration_platform_id,
        project_registration_platform_id: row.id,
        // Syncs logged before installs were named have no install: they belong to the first install.
        include_unassigned: !!row.primary,
        // The title of a sync's records dialog
        platformName: this.platformName(row),
      },
    });
    modelRef.result.then(() => undefined, () => undefined);
  }

  syncStatus(): void {
    this.registrationPlatformStore.dispatch(InfluencersSyncStatus({syncJobId: this.importJobId}));
  }

  isImportDataFilled(): boolean {
    return this.importData && Object.keys(this.importData).length > 0;
  }

  importDataTableColumns() {
    return this.importData['columns'];
  }

  /** The refused records of a sync, and the table built from them (null clears both). */
  setImportData(data: any): void {
    this.importData = data;
    const fields: string[] = data?.columns || [];
    this.importColumns = fields.map((field, index) => ({id: `field_${index}`, header: field, className: 'delete-log'}));
    // The row's own fields stay under `record`, so a field called id still shows its value.
    this.importRows = (data?.list || []).map((record: any, index: number) => ({id: index, record}));
  }

  /** The records a sync refused ("Invalid Records"). */
  showSyncLogs(): void {
    this.modalService.open(this.logsTable(), {
      centered: false,
      size: 'xl',
      keyboard: false,
      backdrop: 'static',
      windowClass: 'api-platform-auth-modal',
    }).result.then(() => undefined, () => undefined);
  }

  private subscribeStores(): void {
    this.registrationPlatformStore.pipe(select(getSyncInfluencers))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((influencersSync: any) => {
        if (!influencersSync || influencersSync === true) {
          return;
        }
        // Sync status response for a job that failed in the task runner: the backend
        // returns the job's failure reason so the user can see why the sync failed.
        if (influencersSync.status === 'failed' && influencersSync.failedReason) {
          this.endSync();
          this.toastr.error(influencersSync.failedReason, 'Sync failed');
          return;
        }
        if (influencersSync.success) {
          return;
        }
        this.importJobId = influencersSync.data?.jobId;
        let errorMessage = '';
        if (influencersSync.error?.name === 'AppTimeoutError') {
          errorMessage = 'Upload timeout';
        }
        if (influencersSync.error?.name === 'AppError') {
          errorMessage = influencersSync.message;
        }
        if (influencersSync.error?.name === 'AppValidationError') {
          errorMessage = influencersSync.error.orgMessage || 'Invalid Records';
          this.setImportData(influencersSync.data);
          this.showSyncLogs();
        }
        // Any other refusal (a 4xx body with only `message`) ends the sync too.
        if (!errorMessage && influencersSync.success === false) {
          errorMessage = influencersSync.message || 'The sync could not be started.';
        }
        if (errorMessage) {
          this.endSync();
          this.toastr.error(errorMessage, 'Error');
        }
      });

    this.registrationPlatformStore.pipe(select(getInfluencersSyncError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((error) => {
        if (error) {
          this.endSync();
          this.toastr.error(error, 'Error');
        }
      });
  }

  /** Sync progress from the socket, for this page's sync or one started elsewhere. */
  private subscribeToSocket(): void {
    this.socketSubscription = this.socketService.sourceData.subscribe((message: any) => {
      const data = message?.data;
      if (data?.requesterType !== 'influencer_sync' || (this.importJobId && data.id !== this.importJobId)) {
        return;
      }
      this.loaderImport.next(true);
      // The install the job names; an older job names only its platform.
      this.currentSyncId.set(Number(data.project_registration_platform_id) || this.primaryInstallOf(Number(data.platform_id)));
      this.importJobId = data.id;
      this.importProgress.set({status: data.status, progress: data.progress});
      if (!['finished', 'failed'].includes(data.status)) {
        return;
      }
      setTimeout(() => {
        if (data.status === 'failed') {
          this.syncStatus();
        } else {
          this.setImportData(null);
          this.toastr.success('Sync successful', 'Success');
        }
        this.endSync();
        this.load();
      }, 1500);
    });
  }

  private endSync(): void {
    this.loaderImport.next(false);
    this.currentSyncId.set(0);
    this.importJobId = null;
    this.importProgress.set({status: 'started', progress: 0});
  }

  /** A sync naming only its platform: that platform's first install in the project. */
  private primaryInstallOf(platformId: number): number {
    const installs = this.platforms().filter((row) => row.registration_platform_id === platformId);
    return (installs.find((row) => row.primary) || installs[0])?.id || 0;
  }

  /** A saved row in place of its install; a saved row does not know whether it is the primary, the list did. */
  private replaceRow(updated: ProjectRegistrationPlatform | null): void {
    if (!updated) {
      return;
    }
    this.platforms.update((platforms) => platforms.map((row) => (
      row.id === updated.id ? {...updated, primary: updated.primary ?? row.primary} : row
    )));
  }

  private escape(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}
